
import { defineComponent, ref, computed, getCurrentInstance, onMounted, onBeforeUnmount, nextTick } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { getServerFileErrorsFromUploadError } from "@/utils/documentUploadErrors";
import { TTask_Template } from "@/models/tasks";
import { SMApp, SMNotifications } from "@/store";
import type { API_TFile } from "@/models/file";
import type { Route } from "vue-router";

export default defineComponent({
  name: "DocumentTaskTemplateActions",

  components: {
    ActionButtonsV2: () => import("@/components/ActionContainer/ActionButtonsV2.vue"),
  },

  props: {
    variant: { type: String as () => "task" | "directory", default: "task" },
    files: { type: Array as () => File[], default: () => [] },
    taskTemplate: { type: Object as () => TTask_Template | null, default: () => null },
    isSaveDisabled: { type: Boolean, default: false },
    isParsedFileType: { type: Boolean, default: false },
    parsedContent: { default: null },
    additionalData: { default: null },
    date: { default: null },
    startDate: { default: null },
    endDate: { default: null },
  },

  emits: ["files", "saved-files", "save-files-to-directory", "filename-validation-error"],

  setup(props, { emit }) {
    const okLabel = computed(() => {
      if (props.isParsedFileType) return "Confirm & Save"
      return "Save"
    })

    const loading = ref(false);

    const savedFiles = ref<API_TFile[]>([]);

    const isDocumentSaved = computed(() => !!savedFiles.value.length);

    const isSaveDisabled = computed(() => {
      if (props.isSaveDisabled) return true;

      if (!props.files.length) return true;

      if (isDocumentSaved.value) return true;

      return false;
    });

    const parsedContent = computed(() => props.parsedContent);

    const additionalData = computed(() => props.additionalData);

    const date = computed(() => props.date);
    const startDate = computed(() => props.startDate);
    const endDate = computed(() => props.endDate);

    const hasUnsavedChanges = computed(
      () => props.files.length > 0 && !isDocumentSaved.value
    );

    async function saveDocumentsDirectory() {
      emit("save-files-to-directory", props.files);
    }

    async function saveDocumentsTask() {
      let object_type = "";
      let object_id = 0;

      if (props.taskTemplate.product_type == "case") {
        object_type = 'case';
        object_id = props.taskTemplate.product_type_id;
      } else if (props.taskTemplate.product_type == "compliance") {
        if (props.taskTemplate.target_user.ID === 0) {
          if (props.taskTemplate.user.role == 'employer') {
            object_type = 'company';
            object_id = props.taskTemplate.company_id;
          } else if (props.taskTemplate.user.role == 'employee') {
            object_type = 'employee'
            object_id = props.taskTemplate.user_id
          }
        } else {
          object_type = 'employee';
          object_id = props.taskTemplate.target_user_id;
        }
      }

      if (!object_type) return console.log("no object type");
      if (!object_id) return console.log("no object id");

      const dtv2 = props.taskTemplate.document_template_v2_id;
      const taskId = props.taskTemplate.id;

      if (!dtv2) return console.log("no doc template id");
      if (!taskId) return console.log("no task id");

      let shouldClearFiles = true;

      try {
        loading.value = true;

        const formData = new FormData();

        props.files.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("task_id", `${taskId}`);
        formData.append("object_type", object_type);
        formData.append("object_id", `${object_id}`);
        formData.append("document_template_id", `${dtv2}`);

        if (additionalData.value)
          formData.append("additional_data", JSON.stringify(additionalData.value));

        if (parsedContent.value) {
          formData.append("document_data", JSON.stringify(parsedContent.value));
        } else if (date.value) {
          formData.append("document_data", JSON.stringify({ date: date.value }));
        } else if (startDate.value && endDate.value) {
          formData.append("document_data", JSON.stringify({ start_date: startDate.value, end_date: endDate.value }));
        }

        const url = API_ROUTES.DOCUMENTS_UPLOAD;

        const { data } = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        savedFiles.value = data.Files;

        emit("saved-files", data.Files);

        // console.log(Object.fromEntries(formData.entries()));

        SMNotifications.fetchNotificationsStatus();
      } catch (error) {
        const fileErrors = getServerFileErrorsFromUploadError(error);

        if (fileErrors) {
          emit("filename-validation-error", fileErrors);
          shouldClearFiles = false;
        }
      } finally {
        if (shouldClearFiles) emit("files", []);

        loading.value = false;
      }
    }

    async function saveDocuments() {
      if (props.variant === "task") {
        saveDocumentsTask();
      } else {
        saveDocumentsDirectory();
      }
    }

    // --- navigation guard

    const isPopupConfirmOpen = ref(false);

    const guardBypassOnce = ref(false);

    const pendingRoute = ref<Route | null>(null);

    function onCancel() {
      if (hasUnsavedChanges.value) {
        isPopupConfirmOpen.value = true;
      } else {
        SMApp.goBack();
      }
    }

    const router = getCurrentInstance()?.proxy?.$router;

    // "Leave Without Saving"
    async function onCancelPopupConfirm() {
      isPopupConfirmOpen.value = false;
      guardBypassOnce.value = true;
      await nextTick();

      if (pendingRoute.value) {
        router.push(pendingRoute.value.fullPath);
        pendingRoute.value = null;
      } else {
        SMApp.goBack();
      }
    }

    // "Stay on Page"
    function onOkPopupConfirm() {
      isPopupConfirmOpen.value = false;
      pendingRoute.value = null;
    }

    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasUnsavedChanges.value) return;

      e.preventDefault();
      (e as any).returnValue = "";
    }

    onMounted(() => {
      window.addEventListener("beforeunload", handleBeforeUnload);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    });

    return {
      okLabel,
      loading,
      savedFiles,
      isDocumentSaved,
      isSaveDisabled,
      saveDocuments,
      onCancel,
      isPopupConfirmOpen,
      onCancelPopupConfirm,
      onOkPopupConfirm,
      parsedContent
    };
  },
});
